/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import {
  ReceiptIcon,
  DeductionIcon,
  NotificationsControlsContainer,
  ControlFilterIcon,
  ControlCalendarIcon,
} from "./styled";
import {
  daysDifferenceWithToday,
  formatDate,
  isBeforeDate,
} from "../../../utils/date";
import { Button } from "../../ui-kit/Button";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { SpinnerContainer } from "../Receipt/components/ReceiptTable/ReceiptDetails/styled";
import { Spinner } from "../../ui-kit/Spinner";
import { NoData } from "../Receipt/components/TableList/styled";
import { AppealRequestParams, IAppeal } from "../../../types/appeal";
import { fetchAppeals, fetchSingleAppeal } from "../../../api/appeals";
import { BadgeContainer } from "../../common/Content/styled";
import { AppealModal } from "./AppealModal";
import { AppealsFilterCalendar } from "./Filters/CalendarDialog";
import { AppealsFilters } from "./Filters/FiltersDialog";

export function AppealsTab() {
  const { t } = useTranslation();
  const [appeals, setAppeals] = useState<IAppeal[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [pending, setPending] = useState(true);
  const [requestParams, setRequestParams] = useState<
    Partial<AppealRequestParams>
  >({ page: 1 });
  const [appeal, setAppeal] = useState<IAppeal | null>(null);
  const [showModal, setShowModal] = useState(false);

  async function getList(resetList = false) {
    try {
      setPending(true);
      const response = await fetchAppeals(requestParams);

      setAppeals(resetList ? response.data : appeals.concat(response.data));
      setHasMore(response.hasNext);
      setPending(false);
    } catch (e) {
      console.error(e);
    } finally {
      setPending(false);
    }
  }

  async function getAppeal(id: string | number) {
    try {
      setPending(true);
      const response = await fetchSingleAppeal(id);
      setAppeal(response);
      setPending(false);
    } catch (e) {
      console.error(e);
    } finally {
      setPending(false);
    }
  }

  function onChangeRequestParams<
    T extends keyof typeof requestParams,
    K extends typeof requestParams[T]
  >({ param, value }: { param: T; value: K | K[] }) {
    if (!value)
      return setRequestParams((params) => {
        delete params[param];
        return { ...params };
      });
    setRequestParams((params) => ({
      ...params,
      [param]: value,
    }));
  }

  async function onRowClick(id: string | number) {
    try {
      await getAppeal(id);
      setShowModal(true);
    } catch (e) {
      console.error(e);
    }
  }

  const showMore = () => {
    onChangeRequestParams({
      param: "page",
      value: (requestParams.page || 0) + 1,
    });
    getList();
  };

  const handleLanguageChanged = useCallback(() => {
    getList();
  }, []);

  useEffect(() => {
    getList(true);
  }, [requestParams.date, requestParams.status]);

  useEffect(() => {
    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, [handleLanguageChanged]);

  return (
    <>
      <NotificationsControl
        onSubmit={({
          param,
          value,
        }: {
          param: keyof typeof requestParams;
          value: any;
        }) =>
          onChangeRequestParams({
            param,
            value,
          })
        }
      />
      {appeals.map((_appeal, i) => (
        <AppealsRow
          _appeal={_appeal}
          pending={pending}
          onRowClick={onRowClick}
        />
      ))}
      {hasMore && (
        <Button style={{ margin: "3rem 0 5rem 0" }} onClick={showMore}>
          {t("Показать еще")}
        </Button>
      )}
      <SpinnerContainer>
        {pending && <Spinner radius="20px" />}
      </SpinnerContainer>
      {appeals.length === 0 && !pending && (
        <NoData>{t("Список обращений пуст")}</NoData>
      )}
      {showModal && appeal && (
        <AppealModal appeal={appeal} onDismiss={() => setShowModal(false)} />
      )}
    </>
  );
}

type AppealsRowProps = {
  _appeal: IAppeal;
  pending: boolean;
  onRowClick: Function;
};
const AppealsRow: React.FC<AppealsRowProps> = ({
  _appeal,
  pending,
  onRowClick,
}) => {
  const { t } = useTranslation();
  const statusesShownAsClosed = ["SENT_PENALTY", "SENT_WARNING"];
  const status = statusesShownAsClosed.includes(_appeal.status)
    ? "CLOSED"
    : _appeal.status;
  return (
    <div
      className={`w-full min-w-0 border-2 border-white grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-3 bg-white rounded-3xl p-8 mb-6 cursor-pointer desktop:grid-cols-[auto_minmax(0,2fr)_minmax(0,9fr)_auto] desktop:gap-x-10 ${
        pending && "pointer-events-none"
      }`}
      onClick={() => onRowClick(_appeal.id)}
    >
      <BadgeContainer className="col-start-1 row-start-1" type={status}>
        {t(status)}
      </BadgeContainer>
      <div className="col-start-2 row-start-1 min-w-0 text-slate-500 text-[14px] font-['Fira_sans']">
        <span>
          {_appeal?.createdDate &&
            formatDate(new Date(_appeal?.createdDate), "dd.MM.yyyy HH:mm")}
        </span>
      </div>
      <div className="col-span-3 col-start-1 row-start-2 min-w-0 break-words text-slate-900 text-[14px] leading-[17px] font-['Fira_sans'] font-medium desktop:col-span-1 desktop:col-start-3 desktop:row-start-1">
        <span>{_appeal?.appealText}</span>
      </div>
      <div
        className="col-start-3 row-start-1 min-w-0 text-right text-[14px] font-['Fira_sans'] font-medium desktop:col-start-4 desktop:whitespace-nowrap"
        style={{
          color: _appeal.daysToExpire > 5 ? "#00A0E3" : "#F95353",
          ...(status === "CLOSED" && { visibility: "hidden" }),
        }}
      >
        {t("Дней осталось", {
          days: _appeal.daysToExpire > 0 ? _appeal.daysToExpire : 0,
        })}
      </div>
    </div>
  );
};

type NotificationsControlProps = {
  onSubmit: Function;
};
const NotificationsControl: React.FC<NotificationsControlProps> = ({
  onSubmit,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  return (
    <NotificationsControlsContainer>
      <ControlFilterIcon onClick={() => setShowFilters(true)} />
      <ControlCalendarIcon onClick={() => setShowCalendar(true)} />
      {showCalendar && (
        <AppealsFilterCalendar
          onSubmit={(date: Date | undefined) => {
            onSubmit({ param: "date", value: date });
            setShowCalendar(false);
          }}
          onDismiss={() => setShowCalendar(false)}
        />
      )}
      {showFilters && (
        <AppealsFilters
          onSubmit={(param: string, value: any) => {
            onSubmit({ param, value });
            setShowFilters(false);
          }}
          onDismiss={() => setShowFilters(false)}
        />
      )}
    </NotificationsControlsContainer>
  );
};
